<template>
  <div class="concrete-root">
    <SectionTitle :title="$t('views.site-technician.concrete.section-title')" />

    <core-spinner v-if="$wait.waiting('isFetchingConcrete')" />

    <div
      v-else-if="!hasPours"
      class="text-center text--secondary pa-6"
    >
      {{ $t('views.site-technician.concrete.empty') }}
    </div>

    <div v-else>
      <v-subheader>
        {{ $t('views.site-technician.concrete.poursTitle') }}
      </v-subheader>

      <v-expansion-panels
        v-model="openPours"
        multiple
      >
        <v-expansion-panel
          v-for="pour in pours"
          :key="pour.gid"
        >
          <v-expansion-panel-header>
            <div class="d-flex flex-wrap align-center pour-header">
              <strong class="mr-3">{{ supplier(pour) }}</strong>
              <span class="text--secondary mr-3">
                {{ $t('views.site-technician.concrete.pour.mixDesign') }} :
                {{ display(pour.mixDesign) }}
              </span>
              <span class="text--secondary mr-3">
                {{ workLocation(pour) }}
              </span>
              <span
                v-if="pour.concreteElement"
                class="text--secondary mr-3"
              >
                {{ display(pour.concreteElement) }}
              </span>
              <span class="text--secondary">
                {{ $t('views.site-technician.concrete.pour.start') }} :
                {{ formatDatetime(pour.startTime, '—') }}
              </span>
            </div>
          </v-expansion-panel-header>

          <v-expansion-panel-content>
            <v-data-table
              dense
              :headers="placementHeaders"
              :items="pour.placements"
              show-expand
              single-expand
              hide-default-footer
              :items-per-page="-1"
              class="row-pointer"
              @click:row="(item, slot) => slot.expand(!slot.isExpanded)"
            >
              <template #item.loadTime="{ item }">
                {{ formatDatetime(item.loadTime, '—') }}
              </template>
              <template #item.unloadTime="{ item }">
                {{ unloadRange(item) }}
              </template>
              <template #item.water="{ item }">
                <span
                  v-if="item.waterAdded"
                  class="success--text"
                >
                  <v-icon x-small>$fas fa-check</v-icon>
                  <template v-if="item.addedWater != null">
                    ({{ item.addedWater }})
                  </template>
                </span>
                <span
                  v-else
                  class="text--secondary"
                  >—</span
                >
              </template>
              <template #item.admixture="{ item }">
                {{ admixtureLabel(item) }}
              </template>
              <template #item.compliance="{ item }">
                <v-chip
                  :color="complianceColor(item.compliance?.name)"
                  small
                  label
                >
                  {{
                    $t(
                      'views.site-technician.concrete.compliance.' +
                        (item.compliance?.name || 'null'),
                    )
                  }}
                </v-chip>
              </template>
              <template #item.samples="{ item }">
                <v-icon
                  v-if="item.hasSamples"
                  color="primary"
                  small
                >
                  $fas fa-vial
                </v-icon>
                <span
                  v-else
                  class="text--secondary"
                  >—</span
                >
              </template>

              <template #expanded-item="{ item }">
                <td :colspan="placementHeaders.length + 1">
                  <div
                    v-if="item.samples && item.samples.length"
                    class="pa-2"
                  >
                    <v-data-table
                      dense
                      :headers="sampleHeaders"
                      :items="item.samples"
                      hide-default-footer
                      :items-per-page="-1"
                    >
                      <template #item.date="{ item: s }">
                        {{ formatDatetime(s.date, '—') }}
                      </template>
                      <template #item.location="{ item: s }">
                        {{ locationLabel(s.location) }}
                      </template>
                      <template #item.datePickup="{ item: s }">
                        {{ formatDatetime(s.datePickup, '—') }}
                      </template>
                      <template #item.pickupLocation="{ item: s }">
                        {{ locationLabel(s.pickupLocation) }}
                      </template>
                    </v-data-table>
                  </div>
                  <div
                    v-else
                    class="text--secondary pa-2"
                  >
                    {{ $t('views.site-technician.concrete.samples.none') }}
                  </div>
                </td>
              </template>
            </v-data-table>
          </v-expansion-panel-content>
        </v-expansion-panel>
      </v-expansion-panels>
    </div>
  </div>
</template>

<script>
import { mapState } from 'vuex'
import { formatDatetime } from '@/utils/date'
import SectionTitle from '@/components/core/SectionTitle.vue'

export default {
  name: 'FreshConcrete',
  components: { SectionTitle },
  data() {
    return {
      openPours: [],
    }
  },
  computed: {
    ...mapState('chantier', ['pours']),
    hasPours() {
      return (this.pours || []).length > 0
    },
    placementHeaders() {
      return [
        {
          text: this.$t(
            'views.site-technician.concrete.placements.headers.truck',
          ),
          value: 'truck',
        },
        {
          text: this.$t(
            'views.site-technician.concrete.placements.headers.invoice',
          ),
          value: 'invoice',
        },
        {
          text: this.$t(
            'views.site-technician.concrete.placements.headers.volume',
          ),
          value: 'volumeDelivered',
        },
        {
          text: this.$t(
            'views.site-technician.concrete.placements.headers.air',
          ),
          value: 'airContent1',
        },
        {
          text: this.$t(
            'views.site-technician.concrete.placements.headers.slump',
          ),
          value: 'slump1',
        },
        {
          text: this.$t(
            'views.site-technician.concrete.placements.headers.unitWeight',
          ),
          value: 'unitWeight',
        },
        {
          text: this.$t(
            'views.site-technician.concrete.placements.headers.temperature',
          ),
          value: 'temperature',
        },
        {
          text: this.$t(
            'views.site-technician.concrete.placements.headers.loadTime',
          ),
          value: 'loadTime',
        },
        {
          text: this.$t(
            'views.site-technician.concrete.placements.headers.unloadTime',
          ),
          value: 'unloadTime',
        },
        {
          text: this.$t(
            'views.site-technician.concrete.placements.headers.water',
          ),
          value: 'water',
          sortable: false,
        },
        {
          text: this.$t(
            'views.site-technician.concrete.placements.headers.admixture',
          ),
          value: 'admixture',
          sortable: false,
        },
        {
          text: this.$t(
            'views.site-technician.concrete.placements.headers.compliance',
          ),
          value: 'compliance',
          sortable: false,
        },
        {
          text: this.$t(
            'views.site-technician.concrete.placements.headers.samples',
          ),
          value: 'samples',
          sortable: false,
        },
      ]
    },
    sampleHeaders() {
      return [
        {
          text: this.$t(
            'views.site-technician.concrete.samples.headers.number',
          ),
          value: 'sampleNumber',
        },
        {
          text: this.$t(
            'views.site-technician.concrete.samples.headers.quantity',
          ),
          value: 'quantity',
        },
        {
          text: this.$t('views.site-technician.concrete.samples.headers.date'),
          value: 'date',
        },
        {
          text: this.$t(
            'views.site-technician.concrete.samples.headers.location',
          ),
          value: 'location',
          sortable: false,
        },
        {
          text: this.$t(
            'views.site-technician.concrete.samples.headers.pickupDate',
          ),
          value: 'datePickup',
        },
        {
          text: this.$t(
            'views.site-technician.concrete.samples.headers.pickupLocation',
          ),
          value: 'pickupLocation',
          sortable: false,
        },
      ]
    },
  },
  methods: {
    formatDatetime,
    display(value) {
      return value === null || value === undefined ? '—' : value
    },
    supplier(pour) {
      return pour.supplierName || pour.supplierOther || '—'
    },
    workLocation(pour) {
      const parts = []
      if (pour.workType?.labelFr) parts.push(pour.workType.labelFr)
      if (pour.level != null) parts.push(pour.level)
      if (pour.section != null) parts.push(pour.section)
      return parts.length ? parts.join(' · ') : '—'
    },
    locationLabel(loc) {
      if (!loc) return '—'
      const parts = []
      if (loc.station != null) parts.push(loc.station)
      if (loc.latitude != null && loc.longitude != null) {
        parts.push(`(${loc.latitude}, ${loc.longitude})`)
      }
      return parts.length ? parts.join(' ') : '—'
    },
    unloadRange(item) {
      const start = this.formatDatetime(item.unloadStartTime, '')
      const end = this.formatDatetime(item.unloadEndTime, '')
      if (!start && !end) return '—'
      if (start && end) return `${start} – ${end}`
      return start || end
    },
    admixtureLabel(item) {
      const parts = []
      if (item.addedAdmixture1 != null) parts.push(item.addedAdmixture1)
      if (item.addedAdmixture2 != null) parts.push(item.addedAdmixture2)
      return parts.length ? parts.join(' / ') : '—'
    },
    complianceColor(name) {
      switch (name) {
        case 'C':
          return 'success'
        case 'AC':
          return 'warning'
        case 'NC':
          return 'error'
        default:
          return 'grey lighten-2'
      }
    },
  },
}
</script>

<style scoped>
.concrete-root {
  flex: 1 1 auto;
  min-height: 0;
  overflow-y: auto;
  padding: 16px;
}

.pour-header {
  gap: 4px;
}
</style>
